"use client";

import { useEffect, useState, useCallback, memo, Suspense, lazy } from "react";
import { Article } from "@/types";
import HomeSkeleton from "@/components/skeleton/HomeSkeleton";
// import ErrorBoundary from "@/components/ErrorBoundary"; // Uncomment after creating the component

// Lazy load non-critical components for better performance
const NewsCarousel = lazy(() => import("@/components/home/NewsCarousel"));
const FeatureNews = lazy(() => import("@/components/home/FeatureNews"));
const AnalyseSection = lazy(() => import("@/components/home/AnalyseSection"));
const FollowUs = lazy(() => import("@/components/home/FollowUs"));
const MostView = lazy(() => import("@/components/home/MostView"));
const International = lazy(() => import("@/components/home/InternationalNews"));
const NewsSection = lazy(() => import("@/components/NewsSection"));
const PostsSection = lazy(() => import("@/components/home/PostsSection"));
const LatestVideo = lazy(() => import("@/components/home/LatestVideo"));

// Import critical above-the-fold component normally
import ResponsiveFeaturedSection from "@/components/home/ResponsiveFeaturedSection";


// Temporary wrapper component (replace with ErrorBoundary when available)
const SafeSection = memo(({ children, fallback }: { children: React.ReactNode; fallback?: React.ReactNode }) => {
  try {
    return <>{children}</>;
  } catch (error) {
    console.error("Section error:", error);
    return fallback || <SectionSkeleton />;
  }
});

SafeSection.displayName = "SafeSection";
const SectionSkeleton = memo(({ height = "h-64" }: { height?: string }) => (
  <div className={`${height} w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700`} />
));

SectionSkeleton.displayName = "SectionSkeleton";

const CarouselSkeleton = memo(() => (
  <div className="hidden sm:block">
    <div className="h-48 w-full animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
  </div>
));

CarouselSkeleton.displayName = "CarouselSkeleton";

// Error state component
const ErrorState = memo(({ 
  error, 
  onRetry 
}: { 
  error: string; 
  onRetry: () => void; 
}) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="max-w-md text-center">
      <div className="mb-6">
        <svg 
          className="mx-auto h-16 w-16 text-red-500" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
          />
        </svg>
      </div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900 dark:text-white">
        Erreur de chargement
      </h1>
      <p className="mb-6 text-gray-600 dark:text-gray-400">{error}</p>
      <button
        onClick={onRetry}
        className="rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Réessayer
      </button>
    </div>
  </div>
));

ErrorState.displayName = "ErrorState";

// Empty state component
const EmptyState = memo(() => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="max-w-md text-center">
      <div className="mb-6">
        <svg 
          className="mx-auto h-16 w-16 text-gray-400" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
          />
        </svg>
      </div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900 dark:text-white">
        Aucun contenu disponible
      </h1>
      <p className="text-gray-600 dark:text-gray-400">
        Aucun article disponible pour le moment. Revenez plus tard pour découvrir nos dernières actualités.
      </p>
    </div>
  </div>
));

EmptyState.displayName = "EmptyState";

// Main sections wrapper component
const MainSections = memo(({ articles }: { articles: Article[] }) => (
  <>
    {/* Hero Carousel - Hidden on mobile */}
    <SafeSection fallback={<CarouselSkeleton />}>
      <Suspense fallback={<CarouselSkeleton />}>
        <div className="hidden sm:block">
          <NewsCarousel articles={articles} />
        </div>
      </Suspense>
    </SafeSection>

    {/* Main Content Container */}
    <section className="container mx-auto py-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Main Section (2/3 width) */}
        <div className="md:col-span-2">
          <SafeSection fallback={<SectionSkeleton height="h-96" />}>
            <ResponsiveFeaturedSection articles={articles} />
          </SafeSection>
        </div>

        {/* Sidebar (1/3 width) */}
        <aside className="flex flex-col space-y-6">
          {/* Posts Section (Hidden on mobile) */}
          <SafeSection fallback={<SectionSkeleton />}>
            <Suspense fallback={<SectionSkeleton />}>
              <div className="hidden sm:block">
                <PostsSection />
              </div>
            </Suspense>
          </SafeSection>

          {/* Latest Video */}
          <SafeSection fallback={<SectionSkeleton />}>
            <Suspense fallback={<SectionSkeleton />}>
              <LatestVideo />
            </Suspense>
          </SafeSection>
        </aside>
      </div>
    </section>

    {/* Featured News Section */}
    <SafeSection fallback={<SectionSkeleton />}>
      <Suspense fallback={<SectionSkeleton />}>
        <section className="container mx-auto p-4">
          <FeatureNews />
        </section>
      </Suspense>
    </SafeSection>

    {/* Analysis and Social Section */}
    <section className="container mx-auto grid grid-cols-1 gap-6 px-4 py-6 md:grid-cols-3">
      <div className="md:col-span-2">
        <SafeSection fallback={<SectionSkeleton />}>
          <Suspense fallback={<SectionSkeleton />}>
            <AnalyseSection />
          </Suspense>
        </SafeSection>
      </div>
      
      <aside className="space-y-6">
        <SafeSection fallback={<SectionSkeleton height="h-48" />}>
          <Suspense fallback={<SectionSkeleton height="h-48" />}>
            <FollowUs />
          </Suspense>
        </SafeSection>
        
        <SafeSection fallback={<SectionSkeleton />}>
          <Suspense fallback={<SectionSkeleton />}>
            <MostView />
          </Suspense>
        </SafeSection>
      </aside>
    </section>

    {/* General News Section */}
    <SafeSection fallback={<SectionSkeleton height="h-96" />}>
      <Suspense fallback={<SectionSkeleton height="h-96" />}>
        <NewsSection />
      </Suspense>
    </SafeSection>

    {/* International News Section */}
    <SafeSection fallback={<SectionSkeleton />}>
      <Suspense fallback={<SectionSkeleton />}>
        <section className="container mx-auto p-4">
          <International />
        </section>
      </Suspense>
    </SafeSection>
  </>
));

MainSections.displayName = "MainSections";

const HomePage = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch articles with retry mechanism
  const fetchArticles = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/articles`, {
        headers: {
          'Content-Type': 'application/json',
        },
        // Add timeout and cache control
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const json = await response.json();
      
      if (json.error) {
        throw new Error(json.error);
      }

      if (!json.articles || !Array.isArray(json.articles)) {
        throw new Error("Format de données invalide");
      }

      setArticles(json.articles);
    } catch (err) {
      console.error("Erreur lors du chargement des articles:", err);
      
      // Retry logic
      if (retryCount < maxRetries && err instanceof Error) {
        console.log(`Tentative ${retryCount + 1}/${maxRetries + 1}...`);
        setTimeout(() => fetchArticles(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      
      setError(
        err instanceof Error 
          ? err.message === "The operation was aborted due to timeout"
            ? "Délai d'attente dépassé. Vérifiez votre connexion."
            : err.message
          : "Impossible de récupérer les articles."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleRetry = useCallback(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Performance monitoring
  useEffect(() => {
    if (typeof window !== "undefined" && "performance" in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === "navigation") {
            console.log("Page load time:", entry.duration);
          }
        }
      });
      observer.observe({ entryTypes: ["navigation"] });
      
      return () => observer.disconnect();
    }
  }, []);

  // Show loading state
  if (loading && articles.length === 0) {
    return <HomeSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState error={error} onRetry={handleRetry} />;
  }

  // Show empty state
  if (articles.length === 0) {
    return <EmptyState />;
  }

  return (
    <main className="min-h-screen bg-white dark:bg-gray-900">
      <MainSections articles={articles} />
    </main>
  );
};

export default memo(HomePage);
